import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Ref } from 'vue';

export interface IFloorPlanGrid {
  gridCols: number;
  gridRows: number;
}

export interface IFloorPlanCellOptions {
  /** Smallest cell in px; wider grids overflow instead of shrinking further. */
  minCell?: number;
  /** Tallest grid as a share of the window height, never below `minHeight`. */
  heightRatio?: number;
  minHeight?: number;
  /** Horizontal room (px) kept around the grid, e.g. for a focus ring. */
  inset?: number;
}

/**
 * Width-driven cell size for a sector grid drawn inside `container`: the grid
 * spans the container width, capped by a viewport-based height so tall grids
 * never produce endless canvases. `minCell` keeps tiles tappable on narrow
 * screens; the caller then lets the grid scroll sideways. A picker inside a
 * modal passes a smaller `heightRatio` so the fields below stay reachable.
 *
 * Manual ResizeObserver: vueuse's element-size helpers warn under Vue 2.7
 * (their useMounted passes null as the onMounted target).
 */
export function useFloorPlanCells(
  container: Ref<HTMLElement | null>,
  grid: () => IFloorPlanGrid,
  options: IFloorPlanCellOptions = {}
) {
  const {
    minCell = 0,
    heightRatio = 0.6,
    minHeight = 360,
    inset = 0,
  } = options;
  const containerW = ref(0);
  const windowH = ref(window.innerHeight);
  let resizeObserver: ResizeObserver | null = null;

  function onWindowResize() {
    windowH.value = window.innerHeight;
  }

  onMounted(() => {
    resizeObserver = new ResizeObserver(([entry]) => {
      containerW.value = entry.contentRect.width;
    });
    if (container.value) resizeObserver.observe(container.value);
    window.addEventListener('resize', onWindowResize);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    window.removeEventListener('resize', onWindowResize);
  });

  const cellPx = computed(() => {
    if (!containerW.value) return 0;
    const { gridCols, gridRows } = grid();
    const maxGridH = Math.max(minHeight, windowH.value * heightRatio);
    const fit = Math.min(
      (containerW.value - inset) / gridCols,
      maxGridH / gridRows
    );
    return Math.max(fit, minCell);
  });

  const gridStyle = computed(() => {
    const cell = cellPx.value;
    const { gridCols, gridRows } = grid();
    return {
      width: `${cell * gridCols}px`,
      height: `${cell * gridRows}px`,
      backgroundSize: `${cell}px ${cell}px`,
    };
  });

  return { cellPx, gridStyle };
}
